import axios from './providers/axios';

let _pendingPackagesRequest = null;
let _lastPackagesResponse = null;
let _lastPackagesTime = 0;
const PACKAGES_CACHE_MS = 2000;

const pricingService = {
    /**
     * Kullanıcının mevcut paket durumunu getir (TimeBanner için)
     * @returns {Promise}
     */
    getUserPackageStatus() {
        return this.getPackages();
    },

    /**
     * Şirketin mevcut plan bilgilerini getir (Pricing sayfası left-section için)
     * @param {string} companyId - Şirket ID'si
     * @returns {Promise}
     */
    getCurrentPlan(companyId) {
        return axios.get('/v1/admin/company/package/getCurrentPlan', {
            params: { companyId }
        });
    },

    /**
     * Tüm paketleri getir (TimeBanner'dan pricing'e yönlendirme için)
     * @param {string} urgency - normal, expired, critical, warning, overdue
     * @returns {Promise}
     */
    getPackages(urgency = 'normal') {
        const now = Date.now();
        if (_pendingPackagesRequest) {
            console.log('[pricingService] getPackages() - mevcut istek paylaşılıyor (duplicate engellendi)');
            return _pendingPackagesRequest;
        }
        if (_lastPackagesResponse && (now - _lastPackagesTime) < PACKAGES_CACHE_MS) {
            console.log('[pricingService] getPackages() - cache\'den döndürülüyor (' + (now - _lastPackagesTime) + 'ms önce)');
            return Promise.resolve(_lastPackagesResponse);
        }
        console.log('[pricingService] getPackages() - yeni istek atılıyor, urgency:', urgency);
        _pendingPackagesRequest = axios.get('/v1/admin/package/store', {
            params: { urgency }
        }).then((response) => {
            _lastPackagesResponse = response;
            _lastPackagesTime = Date.now();
            return response;
        }).finally(() => {
            _pendingPackagesRequest = null;
        });
        return _pendingPackagesRequest;
    },

    // ========== YENİ PAKET API METODları ==========

    /**
     * Lisans modal için paket listesini getir
     * @param {string} companyId - Şirket ID'si (opsiyonel - verilirse uygunluk bilgisi döner)
     * @returns {Promise}
     */
    getPackageList(companyId = null) {
        const params = companyId ? { companyId } : {};
        return axios.get('/v1/admin/package/list', { params });
    },

    /**
     * Seçilen paketin detaylarını getir
     * @param {string} packageId - Paket ID'si
     * @param {string} companyId - Şirket ID'si
     * @param {string} companyPackageId - Şirket paket ID'si
     * @returns {Promise}
     */
    getPackageDetails(packageId, companyId,companyPackageId = null) {

        const payload = {
            packageId: packageId,
            companyId: companyId
        };

        // companyPackageId sadece varsa ekle
        if (companyPackageId) {
            payload.companyPackageId = companyPackageId;
        }

        return axios.post('/v1/admin/company/package/getFromId', payload );
    },

    /**
     * Kullanım limitlerini getir
     * @returns {Promise}
     */
    getUsageLimits() {
        return axios.get('/v1/admin/company/package/getUsageLimits');
    },

    /**
     * Billing history'wi getir
     * @returns {Promise}
     */
    getBillingHistory() {
        return axios.get('/v1/admin/company/billing/store');
    },

    /**
     * Fatura bilgilerini getir (Billing address bilgileri)
     * @returns {Promise}
     */
    getBillingInfo() {
        return axios.get('/v1/admin/company/billing/store');
    },

    /**
     * Yeni fatura bilgisi ekle
     * @param {Object} billingData - Fatura bilgileri
     * @returns {Promise}
     */
    addBillingInfo(billingData) {
        return axios.post('/v1/admin/company/billing/add', billingData);
    },

    /**
     * Fatura bilgilerini güncelle
     * @param {Object} billingData - Fatura bilgileri
     * @returns {Promise}
     */
    updateBillingInfo(billingData) {
        return axios.post('/v1/admin/company/billing/update', billingData);
    },

    /**
     * Ödeme metodlarını getir
     * @returns {Promise}
     */
    getPaymentMethods() {
        return axios.get('/v1/admin/company/card/store');
    },

    /**
     * Yeni ödeme yöntemi ekle (ESKİ API - backward compatibility için)
     * @param {Object} cardData
     * @returns {Promise}
     */
    addPaymentMethod(cardData) {
        // Yeni CompanyCard API'sini kullan
        if (this.shouldUseCompanyCardAPI()) {
            return this.addCompanyCard(cardData);
        }

        // Eski API (mevcut implementasyon)
        return axios.post('/v1/admin/company/card/add', cardData);
    },

    /**
     * Ödeme yöntemi sil
     * @param {string} paymentMethodId
     * @returns {Promise}
     */
    deletePaymentMethod(paymentMethodId) {
        return axios.delete(`/v1/admin/company/card/delete/${paymentMethodId}`);
    },

    /**
     * Fatura indir
     * @param {string} invoiceId
     * @returns {Promise}
     */
    downloadInvoice(invoiceId) {
        return axios.get(`/v1/admin/billing/invoices/${invoiceId}/download`, {
            responseType: 'blob'
        });
    },

    /**
     * Transaction history'yi export et
     * @returns {Promise}
     */
    exportTransactions() {
        return axios.get('/v1/admin/billing/transactions/export', {
            responseType: 'blob'
        });
    },

    /**
     * Kartı birincil (primary) yap
     * @param {string} cardId - Kart ID'si
     * @returns {Promise}
     */
    makePrimaryCard(cardId) {
        return axios.put(`/v1/admin/company/card/makePrimary/${cardId}`);
    },

    // ========== YENİ COMPANYCARD API METODları ==========

    /**
     * Şirket kartlarını listele (CompanyCard API)
     * @param {number} page - Sayfa numarası
     * @param {number} limit - Sayfa başına kayıt sayısı
     * @returns {Promise}
     */
    getCompanyCards(page = 1, limit = 10) {
        return axios.get('/v1/admin/company/card/store', {
            params: { page, limit }
        });
    },

    /**
     * Şirket kartı ekle (3D Secure ile)
     * @param {Object} cardData - Kart bilgileri
     * @returns {Promise}
     */
    addCompanyCard(cardData) {
        const requestData = {
            cardAlias: cardData.cardAlias,
            cardHolderName: cardData.cardHolderName,
            cardNumber: cardData.cardNumber,
            expiryMonth: cardData.expiryMonth,
            expiryYear: cardData.expiryYear,
            cvv: cardData.cvv,
            isPrimary: cardData.isPrimary || false,
            open3d: cardData.open3d !== undefined ? cardData.open3d : true,
        };

        return axios.post('/v1/admin/company/card/add', requestData);
    },

    /**
     * Şirket kartı detayını getir
     * @param {string} cardUuid - Kart UUID'si
     * @returns {Promise}
     */
    getCompanyCard(cardUuid) {
        return axios.get(`/v1/admin/company/card/${cardUuid}`);
    },

    /**
     * Şirket kartını sil
     * @param {string} cardUuid - Kart UUID'si
     * @returns {Promise}
     */
    deleteCompanyCard(cardUuid) {
        return axios.delete(`/v1/admin/company/card/${cardUuid}`);
    },

    /**
     * Kartı primary yap (CompanyCard API)
     * @param {string} cardUuid - Kart UUID'si
     * @returns {Promise}
     */
    makeCompanyCardPrimary(cardUuid) {
        return axios.post(`/v1/admin/company/card/${cardUuid}/make-primary`);
    },

    /**
     * Kart durumunu değiştir (aktif/pasif)
     * @param {string} cardUuid - Kart UUID'si
     * @returns {Promise}
     */
    toggleCompanyCardStatus(cardUuid) {
        return axios.post(`/v1/admin/company/card/${cardUuid}/toggle-status`);
    },

    /**
     * Kayıtlı kart ile ödeme
     * @param {string} cardUuid - Kart UUID'si
     * @param {Object} paymentData - Ödeme bilgileri
     * @returns {Promise}
     */
    payWithCompanyCard(cardUuid, paymentData) {
        const requestData = {
            amount: paymentData.amount,
            installment: paymentData.installment || 0,
        };

        return axios.post(`/v1/admin/company/card/${cardUuid}/payment`, requestData);
    },

    // ========== HELPER METODlar ==========

    /**
     * Eski API'den yeni API'ye geçiş helper'ı
     * @returns {boolean}
     */
    shouldUseCompanyCardAPI() {
        // Bu flag ile hangi API'yi kullanacağımızı kontrol edebiliriz
        return process.env.VUE_APP_USE_COMPANY_CARD_API === 'true' || true;
    },

    /**
     * Helper: Banner tipi belirle
     * @param {string} urgency
     * @returns {string|null}
     */
    getBannerType(urgency) {
        const bannerMapping = {
            'normal': 'trial-ending',
            'warning': 'trial-ending',
            'critical': 'trial-expired',
            'expired': 'payment-reminder',
            'overdue': 'payment-reminder',
            'user-limit': 'new-user',
            'payment_pending': 'payment-pending'
        };
        return bannerMapping[urgency] || null;
    },

    /**
     * Helper: Current plan data'sından kullanıcı bilgilerini çıkar
     * @param {Object} currentPlanData
     * @returns {Object}
     */
    extractUserInfoFromPlan(currentPlanData) {
        if (!currentPlanData?.availableAddonProducts) {
            return { totalUsers: 100, usedUsers: 8, remainingUsers: 92 };
        }

        // Mobile app users veya admin users addon'unu bul
        const userAddon = currentPlanData.availableAddonProducts.find(
            addon => addon.product.productKey === 'mobile_app_core' ||
                addon.product.productKey === 'new_admin_add'
        );

        const totalUsers = userAddon ?
            parseInt(userAddon.packageProductInfo.limitValue) : 100;

        // Used users bilgisi ayrı bir endpoint'ten gelecek (şimdilik sabit)
        const usedUsers = 8;
        const remainingUsers = totalUsers - usedUsers;

        return {
            totalUsers,
            usedUsers,
            remainingUsers,
            usagePercentage: Math.round((usedUsers / totalUsers) * 100)
        };
    },

    /**
     * Helper: Kullanım verilerini formatla
     * @param {Object} usageData
     * @returns {Array}
     */
    formatUsageData(usageData) {
        return usageData?.map(usage => ({
            id: usage.id,
            name: usage.name,
            current: usage.currentUsage,
            limit: usage.limit,
            percentage: (usage.currentUsage / usage.limit) * 100,
            isOverLimit: usage.currentUsage > usage.limit,
            unit: usage.unit
        })) || [];
    },

    /**
     * Helper: Billing data'nın boş olup olmadığını kontrol et
     * @param {Object|Array} billingData - Store'dan gelen billing data
     * @returns {boolean}
     */
    isBillingDataEmpty(billingData) {
        // Array ise
        if (Array.isArray(billingData)) {
            return billingData.length === 0;
        }

        // Object ise - önemli alanlar var mı kontrol et
        if (billingData && typeof billingData === 'object') {
            const requiredFields = ['firstName', 'lastName', 'addressLine'];
            return requiredFields.some(field =>
                !billingData[field] || billingData[field].toString().trim() === ''
            );
        }

        // null, undefined veya falsy ise
        return !billingData;
    },

    /**
     * Helper: Add veya Update modal'ı açılacağını belirle
     * @param {Object|Array} billingData - Store'dan gelen billing data
     * @returns {string} 'add' | 'update'
     */
    getBillingModalMode(billingData) {
        return this.isBillingDataEmpty(billingData) ? 'add' : 'update';
    },

    /**
     * Helper: Paket detayından ürün bilgilerini çıkar ve modal formatına çevir
     * @param {Object} packageDetails - Backend'den gelen paket detayları
     * @returns {Array} Modal'da kullanılacak ürün listesi
     */
    extractProductsFromPackage(packageDetails) {
        try {
            if (!packageDetails?.products) {
                console.warn('Package details missing products');
                return [];
            }

            const extractedProducts = packageDetails.products
                .filter(product => product.isChargeable === true)
                .map(product => {
                    const baseProduct = {
                        productId: product.productId,
                        productKey: product.productKey,
                        name: product.name || product.productKey,
                        limitType: product.limitType,
                        limitValue: product.limitValue
                    };

                    if (product.limitType === 'isCheck') {
                        return {
                            ...baseProduct,
                            isEnabled: product.limitValue === 'true',
                            unit: null
                        };
                    } else {
                        return {
                            ...baseProduct,
                            unit: this.getUnitFromLimitType(product.limitType),
                            quantity: parseInt(product.limitValue) || 0,
                            displayText: `${product.limitValue || 0} ${this.getUnitFromLimitType(product.limitType)}`
                        };
                    }
                });

            return extractedProducts;

        } catch (error) {
            console.error('Error extracting products from package:', error);
            return [];
        }
    },

    /**
     * Helper: Backend limitType'ını frontend unit'e çevir
     * @param {string} limitType
     * @returns {string}
     */
    getUnitFromLimitType(limitType) {
        const unitMapping = {
            'GB': 'GB',
            'count': 'Adet',
            'minute': 'Dakika',
            'hour': 'Saat',
            'MB': 'MB',
            'TB': 'TB',
            'isCheck': null
        };

        return unitMapping[limitType] || 'Adet';
    },

    /**
     * Helper: Product'ları backend formatına çevir (kaydetmeden önce)
     * @param {Array} products - Frontend product array'i
     * @returns {Array} Backend formatındaki product array
     */
    formatProductsForBackend(products) {
        return products.map(product => {
            if (product.limitType === 'isCheck') {
                return {
                    productId: product.productId || null,
                    productKey: product.productKey,
                    productName: product.name,
                    limitValue: product.isEnabled ? 'true' : 'false',
                    limitType: 'isCheck'
                };
            } else {
                return {
                    productId: product.productId || null,
                    productKey: product.productKey,
                    productName: product.name,
                    quantity: product.quantity,
                    limitValue: product.quantity.toString(),
                    limitType: this.getLimitTypeFromUnit(product.unit)
                };
            }
        });
    },

    /**
     * Helper: Frontend unit'ini backend limitType'a çevir
     * @param {string} unit
     * @returns {string}
     */
    getLimitTypeFromUnit(unit) {
        const typeMapping = {
            'GB': 'GB',
            'MB': 'MB',
            'TB': 'TB',
            'Adet': 'count',
            'Dakika': 'minute',
            'Saat': 'hour'
        };

        return typeMapping[unit] || 'count';
    },

    /**
     * Paket ödemesi yap
     * @param {Object} paymentData - Ödeme bilgileri
     * @param {string} paymentData.planId - Paket UUID'si
     * @param {string} paymentData.billingId - Fatura UUID'si
     * @param {string} paymentData.amount - F'yat
     * @param {string} paymentData.billingPeriod - Faturalama periyodu (monthly/yearly)
     * @param {string} paymentData.cardId - Kart UUID'si
     * @param {number} paymentData.installment - Taksit sayısı (0 = tek çekim)
     * @returns {Promise}
     */
    processPayment(paymentData) {
        return axios.post('/v1/admin/company/package/payPackage', {
            packageId: paymentData.planId,
            billingPeriod: paymentData.billingPeriod || 'yearly',
            cardId: paymentData.cardId,
            installment: paymentData.installment || 0,
            billingId: paymentData.billingId,
            amount: paymentData.amount
        });
    },

    /**
     * Ek ürün satın alma
     * @param {Object} purchaseData
     * @param {Array} purchaseData.additionalProducts - [{productId, quantity}]
     * @param {string} purchaseData.cardId - Kart UUID'si
     * @param {string} purchaseData.billingId - Fatura UUID'si
     * @param {number} purchaseData.installment - Taksit sayısı (0 = tek çekim)
     * @returns {Promise}
     */
    purchaseAddonProducts(purchaseData) {
        return axios.post('/v1/admin/company/package/purchaseAddonProducts', {
            additionalProducts: purchaseData.additionalProducts,
            cardId: purchaseData.cardId,
            billingId: purchaseData.billingId || null,
            installment: purchaseData.installment || 0,
        });
    },

    /**
     * Ek ürün satın alma öncesi pro-rata fiyat preview (DRY-RUN).
     * Karta dokunmaz, DB'ye yazmaz.
     * @param {Array} additionalProducts - [{productId, quantity}]
     * @returns {Promise}
     */
    previewAddonPurchase(additionalProducts) {
        return axios.post('/v1/admin/company/package/previewAddonPurchase', {
            additionalProducts,
        });
    },

    /**
     * Şirketin ödeme geçmişi (base paket + ek ürün satın alımları + yenilemeler).
     * @param {string=} companyId - opsiyonel, default login'li kullanıcının şirketi
     * @returns {Promise}
     */
    getPaymentHistory(companyId) {
        return axios.get('/v1/admin/company/package/getPaymentHistory', {
            params: companyId ? { companyId } : {}
        });
    },

    /**
     * Aktif paketin otomatik yenileme bayrağını aç/kapat.
     * Ek ürünler de aynı değerle senkronlanır.
     * @param {boolean} autoRenew
     * @returns {Promise}
     */
    toggleAutoRenew(autoRenew) {
        return axios.post('/v1/admin/company/package/toggleAutoRenew', { autoRenew });
    },

    getCompanyUsageLimits(companyId) {
        return axios.get('/v1/admin/company/package/getUsageLimits', {
            params: { companyId }
        });
    },

    // ========== AI CREDIT METODları ==========

    /**
     * Şirketin AI kredi limitini ve kullanımını getir
     * @param {string} companyId - Şirket UUID'si
     * @returns {Promise}
     */
    getAiCreditLimit(companyId) {
        return axios.get('/v1/admin/company/package/getAiCreditLimit', {
            params: { companyId }
        });
    },

    /**
     * AI kredi kullanımı bildir (konuşma başına token harcaması)
     * @param {Object} usageData
     * @param {string} usageData.companyId - Şirket UUID'si
     * @param {string} usageData.aiAgentId - Agent ID'si
     * @param {number} usageData.creditsUsed - Harcanan kredi miktarı
     * @returns {Promise}
     */
    useAiCredit(usageData) {
        return axios.post('/v1/admin/company/ai-credit/usage', {
            companyId: usageData.companyId,
            aiAgentId: usageData.aiAgentId,
            creditsUsed: usageData.creditsUsed
        });
    }
};

export default pricingService;
